package ui.campaign.detailv2.widgets

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.*
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import common.frontend.api.Io.Campaign.WeatherPictograms
import compose.assets.Svg
import compose.assets.svg.Square_rounded_badge_outline
import io.github.alexzhirkevich.qrose.QrCodePainter
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import playlist.ImplPlaylist
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.layer_top_detail_editor__summary_application_label
import tools.rememberString
import ui.widget.LazyImage

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun CampaignInfoCardApplication(
    config: ImplPlaylist.AppConfig,
    visuals: List<ImplCampaignInfoCard.Visual>,
    pictograms: List<WeatherPictograms>,
    onClick: ((id: Long) -> Unit)? = null
) {
    val codeTemplate = rememberQrCodePainter("https://game.screensoft.org")
    CampaignInfoCardWrapper(
        icon = Svg.Square_rounded_badge_outline,
        label = Res.string.layer_top_detail_editor__summary_application_label.rememberString(),
        onClick = onClick?.let { { it(config.appId) } }
    ) {
        HorizontalDivider()
        CampaignInfoCardConditionsHeader(config.conditions, pictograms)
        HorizontalDivider()
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            LazyImage(
                id = config.logo,
                alphaBackground = false,
                modifier = Modifier.size(48.dp)
                    .align(Alignment.CenterVertically)
            )
            Text(
                text = config.name,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
        }
        HorizontalDivider()
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            visuals.forEach { visual ->
                Box(
                    Modifier.height(128.dp)
                        .aspectRatio(visual.width.toFloat() / visual.height.toFloat(), true)
                ) {
                    LazyImage(
                        id = visual.id,
                        alphaBackground = false,
                        modifier = Modifier.fillMaxSize()
                    )
                    CodePreview(
                        config = config,
                        params = if (visual.ratio.land) config.codeLand else config.codePort,
                        code = codeTemplate
                    )
                }
            }
        }

    }
}

private fun Int.toPct(): Float = toFloat() / 100F

@Composable
private fun CodePreview(
    config: ImplPlaylist.AppConfig,
    params: ImplPlaylist.CodeConfig,
    code: QrCodePainter
) {
    Canvas(Modifier.fillMaxSize()) {
        val qrSize = ((params.sizePct.toPct()) * minOf(
            size.width,
            size.height
        ))
        val padding = qrSize * (params.offsetPct.toPct() / 2F)

        clipRect {
            translate(
                ((size.width * (params.xPct.toPct())) - (qrSize / 2F)),
                ((size.height * (params.yPct.toPct())) - (qrSize / 2F))
            ) {
                drawRoundRect(
                    color = Color(config.color.bg),
                    size = Size(qrSize, qrSize),
                    cornerRadius = CornerRadius(padding)
                )
                translate(padding, padding) {
                    with(code) {
                        val ratio = (qrSize - (padding * 2F)) / intrinsicSize.width
                        scale(ratio, Offset.Zero) {
                            draw(
                                size = intrinsicSize,
                                colorFilter = ColorFilter.tint(Color(config.color.fg))
                            )
                        }
                    }
                }
            }
        }
    }
}